import { dppPoolNames } from '~constants/pools'
import {
  ExactScopeName,
  useHasEntityRole,
  useHasScope,
} from '~hooks/permissions/use-has-scope'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import { useConstructLinkWithPool } from '~hooks/use-construct-link-with-pool'
import {
  getFeatureToggle,
  dppEarningsEnabled,
  nonProductionFeaturesEnabled,
} from '~utils/feature-toggles'

export function useFinancialSubPages() {
  const isTechnicalManager = useHasEntityRole('vessel', 'technical')
  const isMtUser = useHasScope(ExactScopeName.maerskTankersUser)
  const isPoolPartnerUser = useHasEntityRole('poolpartner', 'user')
  const isVesselEarnings = useHasEntityRole('vessel', 'earnings')

  const hasAframaxPartnerScope = useHasScope('pool:Aframax:partner')
  const hasPanamaxPartnerScope = useHasScope('pool:Panamax:partner')
  const hasSuezmaxPartnerScope = useHasScope('pool:Suezmax:partner')

  const isDppPartner =
    hasAframaxPartnerScope || hasPanamaxPartnerScope || hasSuezmaxPartnerScope
  const hasNonDppPool = useHasNonDppPool()

  const forecastLink = useConstructLinkWithPool('/financials/forecast/:pool')
  const earningsLink = useConstructLinkWithPool('/financials/earnings/:pool')
  const averageDistributionLink = useConstructLinkWithPool(
    '/financials/average-distribution/:pool',
  )
  const hirePaymentsLink = useConstructLinkWithPool(
    '/financials/hire-payments/:pool',
  )
  const poolPointsLink = useConstructLinkWithPool(
    '/financials/poolpoints/:pool',
  )
  const compareVesselsLink = useConstructLinkWithPool(
    '/financials/compare-vessels/:pool',
  )
  const reportsLink = useConstructLinkWithPool('/financials/reports/:pool')

  const subpages: any[] = []

  if (isTechnicalManager) {
    subpages.push({
      title: 'Technical Management',
      link: '/financials/technical-management',
    })
  }

  if (isMtUser || isPoolPartnerUser || isVesselEarnings) {
    subpages.push({
      title: 'Earnings',
      link: earningsLink,
    })
  }

  if (isMtUser || isPoolPartnerUser) {
    subpages.push({
      title: 'Average Distribution',
      link: averageDistributionLink,
    })

    if ((isMtUser || isDppPartner) && getFeatureToggle(dppEarningsEnabled)) {
      subpages.push({
        title: 'Hire Payments',
        link: hirePaymentsLink,
      })
    }

    subpages.push({
      title: 'Forecast',
      link: forecastLink,
    })

    if (hasNonDppPool) {
      subpages.push({
        title: 'Compare Vessels',
        link: compareVesselsLink,
      })

      subpages.push({
        title: 'Reports',
        link: reportsLink,
      })
    }
  }

  if (isMtUser) {
    const forecastIndex = subpages.findIndex(
      (subpage) => subpage.title === 'Forecast',
    )
    subpages.splice(forecastIndex + 1, 0, {
      title: 'Pool Points',
      link: poolPointsLink,
    })
  }

  return subpages
}

function useHasNonDppPool() {
  const { data: me } = useGetMe()
  const userPools = [
    ...(me?.profitCenterPools ?? []),
    ...(me?.activePools ?? []),
  ]

  return userPools.some(
    (pool) => !dppPoolNames.some((dppPool) => dppPool === pool),
  )
}
